import { useEffect, useMemo, useState, Fragment } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import StatusPill from "../components/StatusPill";
import PaymentsModal, { PaymentPill } from "../components/PaymentsModal";
import {
  TIERS, TIER_BY_ID, SPONSOR_TIERS, ACTIVATION_TIERS, WAVES,
  STATUS_OPTIONS, DELIVERABLES, TOTAL_INVENTORY_CR, SPONSOR_RACK_CR, ACTIVATION_RACK_CR,
  effectiveValue, paymentStatus, paidTotal,
} from "../lib/constants";
import { formatINR, formatPercent } from "../lib/format";
import { Plus, Trash2, Pencil, Check, X, Wallet, Search, ArrowUpDown, XCircle, Settings, Save, RotateCcw } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const EMPTY = { brand_name: "", tier: "title", slots_taken: 1, status: "prospect", wave: 1, notes: "", closing_price_cr: null };

const statusRank = (s) => ({ closed: 0, negotiating: 1, prospect: 2 }[s] ?? 3);

const SORT_OPTIONS = [
  { v: "updated_desc",    label: "Recently updated" },
  { v: "created_desc",    label: "Recently added" },
  { v: "brand_asc",       label: "Brand A → Z" },
  { v: "brand_desc",      label: "Brand Z → A" },
  { v: "closing_desc",    label: "Closing value ↓" },
  { v: "closing_asc",     label: "Closing value ↑" },
  { v: "rack_desc",       label: "Rack value ↓" },
  { v: "rack_asc",        label: "Rack value ↑" },
  { v: "slots_desc",      label: "Slots taken ↓" },
  { v: "slots_asc",       label: "Slots taken ↑" },
  { v: "wave_asc",        label: "Wave 1 → 3" },
  { v: "status_priority", label: "Status (closed first)" },
  { v: "updated_asc",     label: "Least recently updated" },
];

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="px-2.5 py-1.5 bg-[#070b16] border border-white/10 text-xs text-zinc-200 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-md"
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  );
}

function TierProgressGrid({ tiers, rows, totalRack, testIdPrefix, allowSlotEdit = false, onSlotsChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = allowSlotEdit && isAdmin;
  const [editingId, setEditingId] = useState(null);
  const [draftSlots, setDraftSlots] = useState("");
  const [busy, setBusy] = useState(false);

  const startEdit = (t) => { setEditingId(t.id); setDraftSlots(String(t.slots)); };
  const cancel = () => { setEditingId(null); setDraftSlots(""); };

  const save = async (tierId) => {
    const n = parseInt(draftSlots, 10);
    if (isNaN(n) || n < 0 || n > 500) {
      toast.error("Slots must be between 0 and 500");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/tiers/${tierId}`, { slots: n });
      toast.success("Slot count updated");
      setEditingId(null);
      onSlotsChanged && (await onSlotsChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const resetOverride = async (tierId) => {
    if (!window.confirm("Reset to the default slot count for this tier?")) return;
    setBusy(true);
    try {
      await api.delete(`/tiers/${tierId}/override`);
      toast.success("Reset to default");
      setEditingId(null);
      onSlotsChanged && (await onSlotsChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6" testId={`${testIdPrefix}-tier-grid`}>
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="overline mb-2">Tier inventory</div>
          <div className="font-display text-xl">Slots filled vs available</div>
          {canEdit && (
            <div className="text-[11px] text-zinc-600 mt-1">
              Click <Settings className="h-2.5 w-2.5 inline align-middle" /> on any tier to change total slots
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="overline mb-1">Rack value</div>
          <div className="font-display text-xl tabular">{formatINR(totalRack)}</div>
        </div>
      </div>
      <div className="space-y-4">
        {tiers.map((t) => {
          const closedRows = rows.filter((r) => r.tier === t.id && r.status === "closed");
          const filled = closedRows.reduce((s, r) => s + r.slots_taken, 0);
          const revenue = closedRows.reduce((s, r) => s + effectiveValue(r), 0);
          const pct = t.slots > 0 ? (filled / t.slots) * 100 : 0;
          const isEditing = editingId === t.id;
          return (
            <div key={t.id} data-testid={`tier-row-${t.id}`}>
              <div className="flex items-baseline justify-between text-sm mb-1.5 gap-3">
                <div className="min-w-0">
                  <span className="font-medium">{t.label}</span>
                  <span className="ml-2 text-zinc-500 text-xs">{t.sublabel}</span>
                  {t.slots_overridden && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded">
                      custom
                    </span>
                  )}
                </div>
                <div className="tabular text-zinc-400 flex items-center gap-2 shrink-0">
                  <span className="text-white">{filled}</span>
                  {" / "}
                  {isEditing ? (
                    <div className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={500}
                        value={draftSlots}
                        onChange={(e) => setDraftSlots(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(t.id); if (e.key === "Escape") cancel(); }}
                        data-testid={`tier-slots-input-${t.id}`}
                        autoFocus
                        className="w-16 px-2 py-0.5 bg-[#070b16] border border-emerald-500/60 text-emerald-200 text-sm text-center focus:outline-none rounded"
                      />
                      <button onClick={() => save(t.id)} disabled={busy} data-testid={`tier-slots-save-${t.id}`}
                        className="p-1 text-emerald-400 hover:bg-emerald-500/10 rounded disabled:opacity-40" aria-label="Save">
                        <Save className="h-3 w-3" />
                      </button>
                      {t.slots_overridden && (
                        <button onClick={() => resetOverride(t.id)} disabled={busy} data-testid={`tier-slots-reset-${t.id}`}
                          className="p-1 text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 rounded disabled:opacity-40"
                          title="Reset to default" aria-label="Reset">
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      )}
                      <button onClick={cancel} disabled={busy} className="p-1 text-zinc-500 hover:text-white rounded" aria-label="Cancel">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span>{t.slots}</span>
                      <span>slots</span>
                      {canEdit && (
                        <button
                          onClick={() => startEdit(t)}
                          data-testid={`tier-slots-edit-${t.id}`}
                          className="ml-1 p-1 text-zinc-600 hover:text-emerald-300 hover:bg-white/5 rounded transition-colors"
                          title="Edit total slots"
                          aria-label="Edit slots"
                        >
                          <Settings className="h-3 w-3" />
                        </button>
                      )}
                    </>
                  )}
                  <span> · </span>
                  <span className="ml-2 text-emerald-400">{formatINR(revenue)}</span>
                  <span className="text-zinc-600"> / {formatINR(t.value_cr * t.slots)}</span>
                </div>
              </div>
              <div className="h-[3px] bg-zinc-900">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BrandRow({ row, editing, draft, setDraft, onEdit, onCancel, onSave, onDelete, onOpenPayments, tierOptions }) {
  const tierMeta = TIER_BY_ID[(editing ? draft.tier : row.tier)] || TIER_BY_ID.title;
  const slots = editing ? draft.slots_taken : row.slots_taken;
  const rackV = tierMeta.value_cr * slots;
  const cp = editing ? draft.closing_price_cr : row.closing_price_cr;
  const effective = (cp != null && cp !== "" && !Number.isNaN(Number(cp))) ? Number(cp) : rackV;
  const hasClosing = cp != null && cp !== "" && !Number.isNaN(Number(cp));
  return (
    <tr className="border-t border-zinc-900 hover:bg-[#161616] transition-colors group" data-testid={`brand-row-${row.id}`}>
      <td className="px-4 py-3">
        {editing ? (
          <input
            value={draft.brand_name}
            onChange={(e) => setDraft({ ...draft, brand_name: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="brand-name-input"
          />
        ) : (
          <span className="font-medium">{row.brand_name}</span>
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <select
            value={draft.tier}
            onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="tier-select"
          >
            {tierOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        ) : (
          <div>
            <div className="text-sm">{tierMeta.label}</div>
            <div className="text-xs text-zinc-500">{tierMeta.sublabel}</div>
          </div>
        )}
      </td>
      <td className="px-4 py-3 tabular text-zinc-400 text-sm">{formatINR(rackV)}</td>
      <td className="px-4 py-3 tabular">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              type="number" step="0.5" min={0} max={10000}
              value={draft.closing_price_cr == null ? "" : +(draft.closing_price_cr * 100).toFixed(4)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") setDraft({ ...draft, closing_price_cr: null });
                else {
                  const lakhs = Math.max(0, parseFloat(v));
                  setDraft({ ...draft, closing_price_cr: Number.isFinite(lakhs) ? lakhs / 100 : null });
                }
              }}
              placeholder={`Rack: ${(rackV * 100).toFixed(0)}`}
              className="w-24 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
              data-testid="closing-price-input"
              title="Closing value in ₹ Lakhs (blank = use rack value)"
            />
            <span className="text-xs text-zinc-500">L</span>
          </div>
        ) : hasClosing ? (
          <div className="leading-tight">
            <div className="text-emerald-400 font-medium">{formatINR(effective)}</div>
            <div className="text-[10px] text-zinc-600">closed</div>
          </div>
        ) : (
          <span className="text-zinc-600 text-sm">—</span>
        )}
      </td>
      <td className="px-4 py-3 tabular">
        {editing ? (
          <input
            type="number" min={1}
            value={draft.slots_taken}
            onChange={(e) => setDraft({ ...draft, slots_taken: Math.max(1, +e.target.value) })}
            className="w-16 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
          />
        ) : (
          row.slots_taken
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <select
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            className="bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="status-select"
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <StatusPill status={row.status} />
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <select
            value={draft.wave}
            onChange={(e) => setDraft({ ...draft, wave: +e.target.value })}
            className="bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
          >
            {WAVES.map((w) => <option key={w.id} value={w.id}>W{w.id} · {w.range}</option>)}
          </select>
        ) : (
          <span className="text-sm text-zinc-300">W{row.wave}</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-zinc-400 max-w-[260px]">
        {editing ? (
          <input
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
          />
        ) : (
          <span className="line-clamp-1">{row.notes || "—"}</span>
        )}
      </td>
      <td className="px-4 py-3">
        {row.status === "closed" ? (
          <button
            onClick={() => onOpenPayments(row)}
            data-testid={`open-payments-${row.id}`}
            className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
            title={`Paid ${formatINR(paidTotal(row))} of ${formatINR(effectiveValue(row))}`}
          >
            <PaymentPill status={paymentStatus(row)} />
            <span className="text-[10px] text-zinc-500 tabular">
              {effectiveValue(row) > 0 ? `${Math.round((paidTotal(row) / effectiveValue(row)) * 100)}%` : ""}
            </span>
          </button>
        ) : (
          <span className="text-zinc-700 text-xs">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <button onClick={onSave} data-testid="row-save" className="p-1.5 text-emerald-400 hover:bg-[#1a1a1a]"><Check className="h-4 w-4" /></button>
            <button onClick={onCancel} data-testid="row-cancel" className="p-1.5 text-zinc-400 hover:bg-[#1a1a1a]"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {row.status === "closed" && (
              <button onClick={() => onOpenPayments(row)} data-testid="row-payments" title="Manage payments" className="p-1.5 text-zinc-300 hover:bg-[#1a1a1a]">
                <Wallet className="h-3.5 w-3.5" />
              </button>
            )}
            <button onClick={onEdit} data-testid="row-edit" className="p-1.5 text-zinc-300 hover:bg-[#1a1a1a]"><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={onDelete} data-testid="row-delete" className="p-1.5 text-red-400 hover:bg-[#1a1a1a]"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

export function BrandSection({ kind, tierOptions, totalRack, allRows, reload, allowSlotEdit = false }) {
  const baseRows = useMemo(() => allRows.filter((r) => r.kind === kind), [allRows, kind]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ ...EMPTY, tier: tierOptions[0].id });
  const [payRow, setPayRow] = useState(null);

  // ── Filter + Sort state ──
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fWave, setFWave] = useState("all");
  const [fTier, setFTier] = useState("all");
  const [fPayment, setFPayment] = useState("all");
  const [sortKey, setSortKey] = useState("updated_desc");

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = baseRows.filter((r) => {
      if (fStatus !== "all" && r.status !== fStatus) return false;
      if (fWave !== "all" && String(r.wave) !== String(fWave)) return false;
      if (fTier !== "all" && r.tier !== fTier) return false;
      if (fPayment !== "all") {
        const ps = paymentStatus(r);
        if (fPayment === "any" && !ps) return false;
        if (fPayment !== "any" && ps !== fPayment) return false;
      }
      if (term) {
        const blob = `${r.brand_name} ${r.notes || ""} ${TIER_BY_ID[r.tier]?.label || ""}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });

    const sorters = {
      brand_asc:      (a, b) => a.brand_name.localeCompare(b.brand_name),
      brand_desc:     (a, b) => b.brand_name.localeCompare(a.brand_name),
      closing_desc:   (a, b) => effectiveValue(b) - effectiveValue(a),
      closing_asc:    (a, b) => effectiveValue(a) - effectiveValue(b),
      rack_desc:      (a, b) => (b.value_cr * b.slots_taken) - (a.value_cr * a.slots_taken),
      rack_asc:       (a, b) => (a.value_cr * a.slots_taken) - (b.value_cr * b.slots_taken),
      slots_desc:     (a, b) => b.slots_taken - a.slots_taken,
      slots_asc:      (a, b) => a.slots_taken - b.slots_taken,
      wave_asc:       (a, b) => (a.wave || 0) - (b.wave || 0),
      updated_desc:   (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      updated_asc:    (a, b) => (a.updated_at || "").localeCompare(b.updated_at || ""),
      created_desc:   (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      status_priority:(a, b) => statusRank(a.status) - statusRank(b.status),
    };
    return [...out].sort(sorters[sortKey] || sorters.updated_desc);
  }, [baseRows, q, fStatus, fWave, fTier, fPayment, sortKey]);

  const closedTotal = baseRows.filter((r) => r.status === "closed").reduce((s, r) => s + effectiveValue(r), 0);

  const clearFilters = () => {
    setQ(""); setFStatus("all"); setFWave("all"); setFTier("all"); setFPayment("all");
  };
  const activeFilterCount =
    (q ? 1 : 0) + (fStatus !== "all" ? 1 : 0) + (fWave !== "all" ? 1 : 0) +
    (fTier !== "all" ? 1 : 0) + (fPayment !== "all" ? 1 : 0);

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); };

  const save = async () => {
    await api.put(`/sponsorships/${editingId}`, draft);
    setEditingId(null);
    await reload();
  };

  const create = async () => {
    if (!newRow.brand_name.trim()) return;
    await api.post("/sponsorships", newRow);
    setNewRow({ ...EMPTY, tier: tierOptions[0].id });
    setAdding(false);
    await reload();
  };

  const remove = async (id) => {
    if (!confirm("Delete this row?")) return;
    await api.delete(`/sponsorships/${id}`);
    await reload();
  };

  return (
    <div className="space-y-5">
      <TierProgressGrid
        tiers={tierOptions}
        rows={rows}
        totalRack={totalRack}
        testIdPrefix={kind}
        allowSlotEdit={allowSlotEdit}
        onSlotsChanged={reload}
      />

      <Card className="overflow-hidden" testId={`${kind}-table-card`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
          <div>
            <div className="overline mb-1">{kind === "sponsor" ? "Sponsors" : "Activation brands"}</div>
            <div className="font-display text-lg">
              {rows.length}
              {rows.length !== baseRows.length && <span className="text-zinc-500"> of {baseRows.length}</span>}
              {" "}entries · {formatINR(closedTotal)} closed
            </div>
          </div>
          <button
            onClick={() => setAdding(true)}
            data-testid={`add-${kind}-btn`}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-4 py-2 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add brand
          </button>
        </div>

        {/* Filter & sort bar */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-zinc-900 bg-white/[0.015]" data-testid={`${kind}-filters`}>
          <div className="relative flex-1 min-w-[220px] max-w-[320px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brand or notes…"
              data-testid={`${kind}-search`}
              className="w-full pl-8 pr-3 py-1.5 bg-[#070b16] border border-white/10 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none rounded-md"
            />
          </div>

          <FilterSelect testId={`${kind}-filter-status`} value={fStatus} onChange={setFStatus}
            options={[["all", "All statuses"], ...STATUS_OPTIONS.map((s) => [s, s[0].toUpperCase() + s.slice(1)])]} />
          <FilterSelect testId={`${kind}-filter-wave`} value={fWave} onChange={setFWave}
            options={[["all", "All waves"], ...WAVES.map((w) => [String(w.id), w.label])]} />
          <FilterSelect testId={`${kind}-filter-tier`} value={fTier} onChange={setFTier}
            options={[["all", "All tiers"], ...tierOptions.map((t) => [t.id, t.label])]} />
          <FilterSelect testId={`${kind}-filter-payment`} value={fPayment} onChange={setFPayment}
            options={[
              ["all", "Payment: any"],
              ["paid", "Paid in full"],
              ["partial", "Partial"],
              ["unpaid", "Unpaid"],
              ["overdue", "Overdue"],
            ]} />

          <div className="ml-auto flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                data-testid={`${kind}-clear-filters`}
                className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-100 px-2 py-1"
              >
                <XCircle className="h-3 w-3" />
                Clear ({activeFilterCount})
              </button>
            )}
            <div className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
              <ArrowUpDown className="h-3 w-3" /> Sort
            </div>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              data-testid={`${kind}-sort`}
              className="px-2.5 py-1.5 bg-[#070b16] border border-white/10 text-xs text-zinc-200 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-md"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-[#181818]">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3 font-medium">Brand</th>
              <th className="px-4 py-3 font-medium">Tier</th>
              <th className="px-4 py-3 font-medium">Rack</th>
              <th className="px-4 py-3 font-medium">Closing ₹L</th>
              <th className="px-4 py-3 font-medium">Slots</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Wave</th>
              <th className="px-4 py-3 font-medium">Notes</th>
              <th className="px-4 py-3 font-medium">Payments</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="border-t border-zinc-900 bg-[#141414]">
                <td className="px-4 py-3">
                  <input autoFocus value={newRow.brand_name} onChange={(e) => setNewRow({ ...newRow, brand_name: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                    placeholder="Nike, Adidas, Asics…"
                    data-testid="new-brand-name" />
                </td>
                <td className="px-4 py-3">
                  <select value={newRow.tier} onChange={(e) => setNewRow({ ...newRow, tier: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="new-tier-select">
                    {tierOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 tabular text-zinc-400 text-sm">{formatINR(TIER_BY_ID[newRow.tier].value_cr * newRow.slots_taken)}</td>
                <td className="px-4 py-3 tabular">
                  <div className="flex items-center gap-1.5">
                    <input type="number" step="0.5" min={0} max={10000}
                      value={newRow.closing_price_cr == null ? "" : +(newRow.closing_price_cr * 100).toFixed(4)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") setNewRow({ ...newRow, closing_price_cr: null });
                        else {
                          const lakhs = Math.max(0, parseFloat(v));
                          setNewRow({ ...newRow, closing_price_cr: Number.isFinite(lakhs) ? lakhs / 100 : null });
                        }
                      }}
                      placeholder={`Rack: ${(TIER_BY_ID[newRow.tier].value_cr * newRow.slots_taken * 100).toFixed(0)}`}
                      title="Closing value in ₹ Lakhs (blank = use rack value)"
                      data-testid="new-closing-price"
                      className="w-24 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                    <span className="text-xs text-zinc-500">L</span>
                  </div>
                </td>
                <td className="px-4 py-3 tabular">
                  <input type="number" min={1} value={newRow.slots_taken} onChange={(e) => setNewRow({ ...newRow, slots_taken: Math.max(1, +e.target.value) })}
                    className="w-16 bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                </td>
                <td className="px-4 py-3">
                  <select value={newRow.status} onChange={(e) => setNewRow({ ...newRow, status: e.target.value })}
                    className="bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="new-status-select">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={newRow.wave} onChange={(e) => setNewRow({ ...newRow, wave: +e.target.value })}
                    className="bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500">
                    {WAVES.map((w) => <option key={w.id} value={w.id}>W{w.id} · {w.range}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input value={newRow.notes} onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-2 py-1.5 text-sm focus:outline-none focus:border-emerald-500" />
                </td>
                <td className="px-4 py-3 text-zinc-700 text-xs">—</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={create} data-testid="new-row-save" className="p-1.5 text-emerald-400 hover:bg-[#1a1a1a]"><Check className="h-4 w-4" /></button>
                    <button onClick={() => setAdding(false)} className="p-1.5 text-zinc-400 hover:bg-[#1a1a1a]"><X className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            )}
            {rows.length === 0 && !adding && (
              <tr><td colSpan={10} className="px-6 py-10 text-center text-zinc-500 text-sm">
                No entries yet. Click <span className="text-emerald-400">Add brand</span> to start.
              </td></tr>
            )}
            {rows.map((r) => (
              <BrandRow
                key={r.id}
                row={r}
                editing={editingId === r.id}
                draft={draft}
                setDraft={setDraft}
                onEdit={() => startEdit(r)}
                onCancel={() => setEditingId(null)}
                onSave={save}
                onDelete={() => remove(r.id)}
                onOpenPayments={(row) => setPayRow(row)}
                tierOptions={tierOptions}
              />
            ))}
          </tbody>
        </table>
      </Card>
      {payRow && (
        <PaymentsModal
          row={payRow}
          onClose={() => setPayRow(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

function DeliverableCell({ v }) {
  if (v === "yes") return <span className="text-emerald-400 font-semibold">✓</span>;
  if (v === "neg") return <span className="text-amber-400">~</span>;
  return <span className="text-zinc-600">—</span>;
}

export function DeliverablesMatrix() {
  return (
    <Card className="overflow-hidden" testId="deliverables-card">
      <div className="px-6 py-5 border-b border-zinc-900">
        <div className="overline mb-1">Deliverables matrix</div>
        <div className="font-display text-lg">What each tier gets</div>
        <div className="mt-2 text-xs text-zinc-500 flex items-center gap-4">
          <span><span className="text-emerald-400 font-semibold">✓</span> Included</span>
          <span><span className="text-amber-400">~</span> Negotiable</span>
          <span><span className="text-zinc-600">—</span> Not included</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#181818] sticky top-0">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3 font-medium min-w-[320px]">Deliverable</th>
              {TIERS.map((t) => (
                <th key={t.id} className="px-3 py-3 font-medium text-center min-w-[80px]">
                  <div className="text-zinc-300">{t.short}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DELIVERABLES.map((section) => (
              <Fragment key={section.section}>
                <tr className="bg-[#0f0f0f]">
                  <td colSpan={11} className="px-4 py-3">
                    <div className="overline text-emerald-400">{section.section}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{section.range}</div>
                  </td>
                </tr>
                {section.rows.map((r, i) => (
                  <tr key={`${section.section}-${i}`} className="border-t border-zinc-900 hover:bg-[#141414] transition-colors">
                    <td className="px-4 py-2.5 text-zinc-200 text-[13px]">{r.name}</td>
                    {r.values.map((v, j) => (
                      <td key={j} className="px-3 py-2.5 text-center">
                        <DeliverableCell v={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Sponsorship() {
  // Deprecated: kept only as a redirect target. The page has been split into
  // /sponsors, /activations, /deliverables. App.js now routes directly to those.
  return null;
}
